import { Plus } from "@phosphor-icons/react";
import Price, { usePrice } from "components/common/Price";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import DotsLoader from "components/common/loaders/DotsLoader";
import ProductCardImage from "components/upsell/cards/ProductCardImage";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { useLocale } from "lib/hooks/useLocale";
import { RecentlyViewedProductCardProps } from "lib/types/recentlyViewed";
import { analyticsEvents, analyticsTypes, eventTypes } from "lib/utils/constants";
import { classNames, publishPostMessage, truncateString } from "lib/utils/helpers";
import { errorToast, successToast } from "lib/utils/toasters";
import React, { useEffect, useRef, useState } from "react";
import UpSellVariantSelectorDialog from "../upsell/dialogs/UpSellVariantsSelectorDialog";
import { getTextColorByBrightness } from "@shopflo/ui/utils";
import { Icon } from "@shopflo/ui/icon";

// Shared across all RecentlyViewedProductCard instances so only one empty-cart
// add can be in flight. Without this, each card mounts its own RESULT listener
// and a single success response can fire every pending card's handler.
let emptyCartAddOwner: symbol | null = null;

const RecentlyViewedProductCard: React.FC<RecentlyViewedProductCardProps> = (props) => {
  const { product, keyIdentifier, position, isLast = false, customisation, onAddedToCart } = props;
  const { t } = useLocale();
  const isEmptyCart = position === "EMPTY_CART";

  const { isLoading, handleAddItemWithResult } = useItemEdit();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const [isVariantSelectorOpen, setIsVariantSelectorOpen] = useState(false);
  const [isPendingEmptyCartAdd, setIsPendingEmptyCartAdd] = useState(false);

  const variant = product?.variants?.[0];
  const { formattedPrice } = usePrice(variant?.currentPrice ?? 0);

  const pendingEmptyCartListener = useRef<(event: MessageEvent) => void>();
  const pendingEmptyCartTimeout = useRef<ReturnType<typeof setTimeout>>();
  const emptyCartAddOwnerRef = useRef<symbol | null>(null);

  const clearPendingEmptyCartAdd = () => {
    if (pendingEmptyCartListener.current) {
      window.removeEventListener("message", pendingEmptyCartListener.current);
      pendingEmptyCartListener.current = undefined;
    }
    if (pendingEmptyCartTimeout.current) {
      clearTimeout(pendingEmptyCartTimeout.current);
      pendingEmptyCartTimeout.current = undefined;
    }
    if (emptyCartAddOwnerRef.current && emptyCartAddOwner === emptyCartAddOwnerRef.current) {
      emptyCartAddOwner = null;
    }
    emptyCartAddOwnerRef.current = null;
    setIsPendingEmptyCartAdd(false);
  };

  useEffect(() => {
    return () => {
      clearPendingEmptyCartAdd();
    };
  }, []);

  const handleAddedToCart = (addedItems: Array<{ variantId: string; quantity: number }>) => {
    publishPostMessage(eventTypes.REMOVE_FROM_RECENTLY_VIEWED_QUEUE, {
      productId: product?.productId,
    });

    sendAnalyticsEvent({
      eventName: analyticsEvents.UPSELL_PRODUCT_ADDED,
      eventType: "click",
      eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
      context: position === "CART" || position === "EMPTY_CART" ? "cart" : "checkout",
      metaData: {
        upsellData: {
          position: position,
          productId: product?.productId,
          UpSellSection: "recently_viewed",
          groupIds: product?.upsellIds,
          upsoldItems: addedItems.map(({ variantId, quantity }) => {
            const selectedVariant = product?.variants?.find((v) => v?.variantId === variantId);
            return {
              productId: product?.productId,
              variantId,
              quantity,
              itemTotal: selectedVariant?.currentPrice,
            };
          }),
        },
      },
    });

    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_ADDED_TO_CART_UI,
      eventType: "click",
      eventFor: [analyticsTypes.FACEBOOK_PIXEL],
      metaData: {},
    });

    successToast(
      t(
        addedItems.length > 1 || addedItems?.[0]?.quantity > 1
          ? "upsell_multiple_product_added"
          : "upsell_product_added",
      ),
    );

    onAddedToCart?.(product?.productId);
  };

  const addItemsToCart = async (items: Array<{ variantId: string; quantity: number }>) => {
    const validItems = items.filter((item) => item?.variantId != null && item?.variantId !== "");
    if (!validItems.length) {
      errorToast(t("unable_to_add"));
      return;
    }

    if (isEmptyCart) {
      if (emptyCartAddOwner || isPendingEmptyCartAdd) {
        return;
      }
      const owner = Symbol("empty-cart-add");
      emptyCartAddOwner = owner;
      emptyCartAddOwnerRef.current = owner;
      setIsPendingEmptyCartAdd(true);
      const handleResult = (event: MessageEvent) => {
        if (event?.data?.type !== eventTypes.ADD_RECENTLY_VIEWED_TO_EMPTY_CART_RESULT) {
          return;
        }
        // Ignore results that belong to another in-flight request (or a stale listener).
        if (emptyCartAddOwner !== owner) {
          return;
        }
        clearPendingEmptyCartAdd();
        if (event?.data?.payload?.success) {
          handleAddedToCart(validItems);
        } else {
          errorToast(t("unable_to_add"));
        }
      };
      pendingEmptyCartListener.current = handleResult;
      window.addEventListener("message", handleResult);
      pendingEmptyCartTimeout.current = setTimeout(() => {
        if (emptyCartAddOwner !== owner) {
          return;
        }
        clearPendingEmptyCartAdd();
        errorToast(t("unable_to_add"));
      }, 5000);
      publishPostMessage(eventTypes.ADD_RECENTLY_VIEWED_TO_EMPTY_CART, {
        items: validItems.map(({ variantId, quantity }) => ({ id: variantId, quantity })),
      });
      return;
    }
    const success = await handleAddItemWithResult(validItems);
    if (success) {
      handleAddedToCart(validItems);
    } else {
      errorToast(t("unable_to_add"));
    }
  };

  const handleAddBtnClick = () => {
    const hasMultipleVariants = Boolean(product?.variants?.length > 1);
    if (hasMultipleVariants) {
      setIsVariantSelectorOpen(true);
      return;
    }
    if (!variant?.variantId) {
      errorToast(t("unable_to_add"));
      return;
    }
    addItemsToCart([{ variantId: variant.variantId, quantity: 1 }]);
  };

  return (
    <>
      <div
        key={keyIdentifier}
        className={classNames(
          "upsell-card relative flex h-fit snap-start flex-col gap-2 shrink-0",
          isEmptyCart ? "w-[110px]" : "w-[196px] cursor-pointer",
        )}>
        {!isLast && !isEmptyCart && (
          <div
            className="absolute top-0 bottom-0 right-[-15px] w-[1px] opacity-40"
            style={{
              backgroundColor: getTextColorByBrightness({
                bgHexColor: customisation?.backgroundColor ?? "#FFFFFF",
                lightOpacity: 24,
              }),
            }}
          />
        )}
        <div
          className={classNames(
            "flex",
            isEmptyCart
              ? "flex-col justify-center items-start self-stretch gap-0.5"
              : "flex-row gap-3 flex-1",
          )}>
          <ProductCardImage
            imageLink={variant?.imageLink}
            backgroundColor={customisation?.backgroundColor}
            variant={isEmptyCart ? "large" : "default"}>
            {isEmptyCart && (
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  handleAddBtnClick();
                }}
                className="absolute bottom-2 right-2 flex flex-col items-start gap-2 rounded-lg bg-primary-dark p-2 text-btnPrimaryText shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)]"
                style={{
                  backgroundColor: customisation?.buttonColor,
                  color: customisation?.buttonTextColor,
                }}>
                <Icon IconComponent={Plus} size="xs" iconProps={{ weight: "bold" }} />
              </button>
            )}
          </ProductCardImage>
          {isEmptyCart ? (
            <div className="flex flex-col gap-1 self-stretch">
              <p
                className="self-stretch overflow-hidden text-ellipsis text-text-xs-medium line-clamp-2 px-1"
                style={{ color: customisation?.textColor }}>
                {truncateString(product?.name, 32)}
              </p>
              <p
                className="self-stretch truncate overflow-hidden text-ellipsis text-text-xs-bold px-1"
                style={{ color: customisation?.textColor }}>
                {formattedPrice}
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col space-y-0">
              <p
                className="mt-1 text-wrap line-clamp-2 text-text-xs-medium text-coal-dark"
                style={{
                  color: customisation?.textColor,
                }}>
                {truncateString(product?.name, 32)}
              </p>
              <Price
                total={variant?.currentPrice ?? 0}
                orientation="horizontal"
                customization={{
                  textColor: customisation?.textColor,
                }}
              />
            </div>
          )}
        </div>
        <div className={classNames("flex flex-row w-full gap-2", isEmptyCart ? "hidden" : "")}>
          <button
            onClick={(e) => {
              e?.stopPropagation();
              handleAddBtnClick();
            }}
            className="flex flex-1 h-8 items-center justify-center space-x-1 rounded-lg bg-transparent border-1 border-yay-dark px-3 text-sm font-medium text-yay-dark"
            style={{
              borderColor: customisation?.buttonColor,
              color: customisation?.buttonColor,
            }}>
            {isLoading ? (
              <DotsLoader color={customisation?.buttonColor ?? "var(--flo-yay-color)"} />
            ) : (
              <>
                <Plus size={16} weight="bold" />
                <span className="text-text-xs-semibold">{t("add").toUpperCase()}</span>
              </>
            )}
          </button>
        </div>
      </div>
      {(isLoading || isPendingEmptyCartAdd) && <OverlaySpinner />}
      <UpSellVariantSelectorDialog
        upsellProduct={product}
        isVariantSelectorOpen={isVariantSelectorOpen}
        closeVariantSelector={() => setIsVariantSelectorOpen(false)}
        position={position}
        onPreviousClick={() => setIsVariantSelectorOpen(false)}
        onSubmit={(itemsData) => {
          setIsVariantSelectorOpen(false);
          const items = Object.entries(itemsData).map(([variantId, quantity]) => ({ variantId, quantity }));
          if (items.length) {
            addItemsToCart(items);
          }
        }}
      />
    </>
  );
};

export default RecentlyViewedProductCard;
